import {
  IDeprecatedExtendedTalk,
  IDeprecatedFeedbackParty,
  IDeprecatedFeedbackPartyPayload,
  IDeprecatedTalkPayload,
  IExtendedTalk,
  IMyEmployees,
  IMyEmployeesPayload,
  IMyPeersPayload,
  ITalk,
  ITalkPayload,
  ITalkPeerSelection,
  ITalkPeerSelectionPayload,
  ITalkQuestionnaire,
  ITalkQuestionnairePayload
} from "src/common/types";
import { javaDateToJS } from "src/helpers";
import { getUser } from "src/redux/actions";
import { getCall, postCall } from "src/services";

const mapPeerSelection = ({ status, dueDate, submissionDate }: ITalkPeerSelectionPayload): ITalkPeerSelection => ({
  dueDate: javaDateToJS(dueDate),
  status,
  submissionDate: submissionDate ? javaDateToJS(submissionDate) : null
});

export const getTalkDetails = (talkId: string): Promise<IExtendedTalk> =>
  new Promise((resolve, reject) => {
    (getCall({ url: `/v1/feedback/talk/${talkId}` }) as Promise<ITalkPayload>)
      .then(talk => {
        const talkDetails = mapTalk(talk);
        const { participantId, supervisorId } = talkDetails;
        Promise.all([getUser(participantId), getUser(supervisorId)])
          .then(([participantDetails, supervisorDetails]) => {
            resolve({
              ...talkDetails,
              participantDetails,
              supervisorDetails
            });
          })
          .catch(err => reject(err));
      })
      .catch(err => reject(err));
  });

export const mapTalk = ({
  participantSignedDate,
  supervisorSignedDate,
  talkCompletionDueDate,
  talkQuestionnaires,
  scheduledDate,
  status,
  peerSelection,
  cycle,
  ...otherDetails
}: ITalkPayload): ITalk => ({
  ...otherDetails,
  cycle: {
    ...cycle,
    config: cycle.config
      ? cycle.config
      : {
          showCancelTalkButton: false,
          showFinalRatingButton: false,
          showReassignSupervisorButton: false,
          showReopenQuestionnaireButton: false,
          showReopenTalkButton: false,
          showReopenAnonymousDsvSummaryButton: false
        }
  },
  participantSignedDate: participantSignedDate ? javaDateToJS(participantSignedDate) : null,
  peerSelection: peerSelection ? mapPeerSelection(peerSelection) : null,
  scheduledDate: scheduledDate ? javaDateToJS(scheduledDate) : null,
  status,
  supervisorSignedDate: supervisorSignedDate ? javaDateToJS(supervisorSignedDate) : null,
  talkCompletionDueDate: javaDateToJS(talkCompletionDueDate),
  talkQuestionnaires: talkQuestionnaires.map(mapTalkQuestionnaires)
});

const mapTalkQuestionnaires = ({
  submissionDate,
  submissionDueDate,
  submissionStatus,
  ...otherDetails
}: ITalkQuestionnairePayload): ITalkQuestionnaire => {
  return {
    ...otherDetails,
    submissionDate: submissionDate ? javaDateToJS(submissionDate) : null,
    submissionDueDate: javaDateToJS(submissionDueDate),
    submissionStatus
  };
};

const mapFeedbackParty = (feedbackParty: IDeprecatedFeedbackPartyPayload): IDeprecatedFeedbackParty => {
  const { questionnaireSubmissionDate, signedDate, additionalComments } = feedbackParty;
  return {
    ...feedbackParty,
    additionalComments,
    /**
     * Mapping old statuses to new ones,
     * will be removed when BE will switch to new ones
     */
    questionnaireSubmissionDate: questionnaireSubmissionDate ? javaDateToJS(questionnaireSubmissionDate) : null,
    signedDate: signedDate ? javaDateToJS(signedDate) : null
  };
};

export const populateTalk = (talk: IDeprecatedTalkPayload): Promise<IDeprecatedExtendedTalk> => {
  const { submitter, receiver, scheduledDate, questionnaireSubmissionDueDate, talkCompletionDueDate } = talk;

  return new Promise(resolve =>
    Promise.all([getUser(submitter.id), getUser(receiver.id)]).then(([submitterDetails, receiverDetails]) =>
      resolve({
        ...talk,
        questionnaireSubmissionDueDate: javaDateToJS(questionnaireSubmissionDueDate),
        receiver: mapFeedbackParty(receiver),
        receiverDetails,
        scheduledDate: scheduledDate ? javaDateToJS(scheduledDate) : null,
        submitter: mapFeedbackParty(submitter),
        submitterDetails,
        talkCompletionDueDate: javaDateToJS(talkCompletionDueDate)
      })
    )
  );
};

export const fetchMyFeedbacks = (): Promise<ITalk[]> =>
  new Promise(resolve =>
    (
      getCall({ url: "/v1/feedback/talk/my-feedbacks" }) as Promise<{
        entities: ITalk[];
      }>
    ).then(({ entities: talks }) => resolve(talks.map(mapTalk)))
  );

export const fetchMyEmployees = (status: "ACTIVE" | "EXPIRED"): Promise<IMyEmployees> =>
  new Promise(resolve =>
    (
      postCall({
        url: "/v1/feedback/talk/my-reports",
        body: {
          filter: {
            filters: [
              {
                property: "status",
                value: status
              }
            ]
          },
          page: 0,
          pageSize: 999,
          sort: {
            order: [
              {
                direction: "ASC",
                property: "firstName"
              }
            ]
          }
        }
      }) as Promise<IMyEmployeesPayload>
    ).then(({ entities, ...other }) =>
      resolve({
        ...other,
        entities: entities.map(({ talks, ...userDetails }) => ({
          ...userDetails,
          talks: talks.map(mapTalk)
        }))
      })
    )
  );

export const fetchMyPeers = (status: "ACTIVE" | "EXPIRED"): Promise<IMyPeersPayload> =>
  new Promise(resolve =>
    (
      postCall({
        url: "/v1/feedback/talk/peer-feedback-requests",
        body: {
          filter: {
            filters: [
              {
                property: "status",
                value: status
              }
            ]
          },
          page: 0,
          pageSize: 999,
          sort: {
            order: [
              {
                direction: "ASC",
                property: "firstName"
              }
            ]
          }
        }
      }) as Promise<IMyPeersPayload>
    ).then(response => resolve({ ...response }))
  );
